import { useEffect, useState } from 'react';
import { api, type UploadListRow } from '../../api/client';
import { DataTable, Select, type DataTableColumn } from '../../components/ui';
import { DownloadOriginalButton } from '../../components/DownloadOriginalButton';
import { parseDbTimestamp } from '../../lib/parseDbTimestamp';
import { formatSast } from './readingChecks';

// Every file received through reading@, commission@ and Field Tools, newest
// first, with what became of its rows. billing:audit only: it shows every
// sender and every realm (billing-api GET /api/v2/uploads enforces that; the
// tab is hidden without the role). Built 2026-10-06 so a sheet like the
// mis-dated October one can be opened from the app instead of from R2.

const WINDOWS = [30, 60, 90, 180, 365];

function outcome(r: UploadListRow): string {
  const parts = [
    r.committed ? `${r.committed} committed` : null,
    r.pending ? `${r.pending} pending` : null,
    r.rejected ? `${r.rejected} rejected` : null,
    r.other ? `${r.other} failed` : null,
  ].filter(Boolean);
  return `${r.rows} ${r.rows === 1 ? 'row' : 'rows'}${parts.length ? `: ${parts.join(', ')}` : ''}`;
}

export default function UploadsPage() {
  const [days, setDays] = useState(60);
  const [rows, setRows] = useState<UploadListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getUploads(days)
      .then((r) => { if (!cancelled) setRows(r.data); })
      .catch((err: Error) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  // Fits a 1440px screen without a sideways scroll (Beau, 2026-10-06). At lg
  // DataTable sizes the table to every column's max-content (min-w-max), so
  // each text column is BOUNDED: a block's max-width clamps its max-content
  // contribution, and the text wraps or truncates inside it. Type and realms
  // ride under the filename instead of taking two more padded columns.
  // Budget at 1440: 256 + 144 + 208 + 208 + ~100 (Download) + 5 x 24 padding
  // = ~1036px against a ~1134px content box.
  //
  // Two primaries, as in AuditLogs: more do not fit at 375px. A filename has
  // no guaranteed break point, so it may break anywhere.
  const columns: DataTableColumn<UploadListRow>[] = [
    {
      key: 'file',
      label: 'File',
      priority: 'primary',
      render: (r) => (
        <span className="block max-w-[16rem] [overflow-wrap:anywhere]">
          <span className="block text-sm text-content">{r.filename}</span>
          <span className="block text-xs text-content-muted mt-0.5">
            {r.kind === 'reading' ? 'Reading' : 'Commissioning'}
            {r.realms.length > 0 && <> · <span className="font-mono">{r.realms.join(', ')}</span></>}
          </span>
        </span>
      ),
    },
    {
      key: 'received',
      label: 'Received (SAST)',
      priority: 'primary',
      render: (r) => {
        const at = parseDbTimestamp(r.received_at);
        const value = at ? formatSast(at.getTime()) : r.received_at;
        return <span className="text-xs text-content-muted max-w-[9rem] truncate block" title={value}>{value}</span>;
      },
    },
    {
      key: 'sender',
      label: 'Sender',
      priority: 'secondary',
      render: (r) => (
        <span className="text-xs text-content-muted max-w-[13rem] truncate block" title={r.sender ?? ''}>{r.sender ?? '-'}</span>
      ),
    },
    {
      key: 'outcome',
      label: 'Rows',
      priority: 'secondary',
      render: (r) => <span className="block max-w-[13rem] text-xs text-content">{outcome(r)}</span>,
    },
  ];

  return (
    <div className="bg-surface-raised rounded-lg border border-border-token">
      <div className="px-6 py-4 border-b border-border-token flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-content">Uploads</h2>
          <p className="text-sm text-content-muted mt-0.5">
            Every reading and commissioning file received, from all senders and realms.
          </p>
        </div>
        <label className="text-sm text-content-muted flex flex-col gap-1 sm:w-48">
          Received in the last
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Received in the last">
            {WINDOWS.map((d) => <option key={d} value={d}>{d} days</option>)}
          </Select>
        </label>
      </div>
      {error && (
        <div className="px-6 py-3 bg-red-50 dark:bg-red-900 border-b border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-100">
          {error}
        </div>
      )}
      {loading ? (
        <div className="px-6 py-12 text-center text-content-muted">Loading uploads…</div>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          rowKey={(r) => `${r.kind}:${r.record_id}`}
          density="compact"
          rowActions={(r) => <DownloadOriginalButton kind={r.kind} id={r.record_id} fallbackName={r.filename} label="Download" />}
          rowActionsLabel="Original"
          emptyState={<p className="px-6 py-12 text-center text-content-muted">No uploads in the last {days} days.</p>}
        />
      )}
    </div>
  );
}
